import { useIsMobile } from "@/hooks/use-media-query";
import { useIsPhone } from "@/hooks/use-is-phone";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

interface ResponsiveModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Omit ONLY when `children` render their own `DialogHeader`/`DialogTitle`
   * (and usually `DialogFooter`) -- the maintenance dialogs do, because their
   * forms decide the title from the mode they are in. That still labels the
   * drawer: vaul is built on the SAME @radix-ui/react-dialog instance this
   * app resolves (one hoisted copy, no nested dep), so a `DialogTitle` inside
   * a `DrawerContent` finds the Radix context and fills its aria-labelledby.
   * With no title here and none in the children the modal is unlabelled. */
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** When false, prevents swipe-to-dismiss on mobile Drawer. Default true. */
  dismissible?: boolean;
  /** When set, closing attempts (ESC, overlay click, swipe) call this instead of closing directly. */
  onInterceptClose?: () => void;
  /** Extra classes on the DIALOG box only -- width, max-height, scrolling.
   * The drawer needs none of it: it is already full-width, capped at 96dvh
   * and scrolling its own body, and an `overflow` of its own would fight
   * vaul's drag gesture. */
  dialogClassName?: string;
  /** The content element itself. Popups that must stay inside the modal's
   * interactive layer portal into it -- outside it they inherit
   * `pointer-events: none` and every click reads as an outside-dismiss. */
  contentRef?: React.Ref<HTMLDivElement>;
}

export function ResponsiveModal({
  open,
  onOpenChange,
  title,
  children,
  footer,
  dismissible = true,
  onInterceptClose,
  dialogClassName,
  contentRef,
}: ResponsiveModalProps) {
  // Width OR device: a phone held in landscape is ~850px wide and fails the
  // 639px width test, but it is ~400px TALL -- the one viewport a centred
  // dialog handles worst once the keyboard is up. useIsPhone catches it
  // (coarse pointer + shorter side <= 550px, see domain/device.ts).
  // Both hooks unconditionally -- `||` would short-circuit the second one and
  // change the hook order between renders.
  const isNarrow = useIsMobile();
  const isPhone = useIsPhone();
  const isMobile = isNarrow || isPhone;

  const handleOpenChange = (next: boolean) => {
    if (!next && onInterceptClose) {
      onInterceptClose();
      return;
    }
    onOpenChange(next);
  };

  if (isMobile) {
    const closeButton = (
      <button
        onClick={() => handleOpenChange(false)}
        className={cn(
          "absolute right-4 h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors",
          // Centred in the header when there is one; against the drawer's top
          // edge (beside the drag handle) when the children own the header.
          title ? "top-1/2 -translate-y-1/2" : "top-4 z-10",
        )}
        aria-label="Close"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
      </button>
    );
    return (
      <Drawer open={open} onOpenChange={handleOpenChange} dismissible={dismissible}>
        <DrawerContent ref={contentRef}>
          {title ? (
            <DrawerHeader className="relative">
              <DrawerTitle>{title}</DrawerTitle>
              {closeButton}
            </DrawerHeader>
          ) : (
            closeButton
          )}
          {/* min-h-0 so this actually shrinks inside DrawerContent's flex
              column -- without it a long form pushes past max-h and the
              submit button ends up off-screen instead of scrolling. */}
          <div className={cn("min-h-0 overflow-y-auto px-4 pb-2", !title && "pt-4")}>
            {children}
          </div>
          {footer && <DrawerFooter>{footer}</DrawerFooter>}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent ref={contentRef} showCloseButton className={dialogClassName}>
        {title && (
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        )}
        {children}
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
